import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Plus, Trash2, Ban, AlertTriangle } from 'lucide-react'
import { apiGet, postJson, patchJson, apiDelete } from '../../lib/api.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import {
  MIN_BLOCK_MIN, SNAP_MIN, blocksOfShift, blockPayload, clamp, fmtMin, minOfTime, snap,
  suggestBlock, tsOfMin,
} from '../../lib/shiftBlocks.js'

/**
 * ห้ามแทรก — the breaks inside ONE doctor shift, dragged on a timeline of that
 * shift's own window.
 *
 * A break is its own `appointment` row (see lib/shiftBlocks.js), so this section
 * saves the moment a drag ends rather than riding along with the drawer's
 * บันทึก: the two write different rows, and a half-saved pair would be worse
 * than either. `onChanged` lets the drawer refetch the month after a write.
 *
 * The timeline is the whole point of the section. A break is a duration inside
 * another duration — two time inputs make you do that arithmetic in your head,
 * where a band you drag against the shift's own ruler shows it. The inputs are
 * still there underneath for the keyboard and for exact minutes.
 *
 * Overlap is prevented while dragging (clamped to the neighbours) rather than
 * rejected on save: two overlapping breaks are not meaningfully different from
 * one longer break, and the day view would draw them stacked and unreadable.
 */
export default function ShiftBlocks({ shift, startTime, endTime, onChanged }) {
  const dateKey = String(shift?.date || '').slice(0, 10)
  const doctorId = Number(shift?.doctor_id) || 0
  const branchId = Number(shift?.branch_id) || 0

  const start = minOfTime(startTime)
  const end = minOfTime(endTime)
  const span = (start != null && end != null && end > start) ? end - start : 0

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmRow, setConfirmRow] = useState(null)
  const [tick, setTick] = useState(0)
  const reqId = useRef(0)

  useEffect(() => {
    if (!dateKey || !branchId || !doctorId) { setRows([]); setLoading(false); return }
    const id = ++reqId.current
    setLoading(true); setErr('')
    apiGet('/api/hr/schedule/block', { branch_id: branchId, date: dateKey, doctor_id: doctorId })
      .then((r) => { if (id === reqId.current) setRows(Array.isArray(r) ? r : (r?.items || [])) })
      .catch((e) => { if (id === reqId.current) { setRows([]); setErr(String(e.message || e)) } })
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }, [dateKey, branchId, doctorId, tick])

  const blocks = useMemo(
    () => blocksOfShift(rows, { doctorId, branchId, start: null, end: null }),
    [rows, doctorId, branchId],
  )

  /* ── dragging ────────────────────────────────────────────────────────────
   * Live state lives in a ref as well as in React state: the pointermove
   * handler is bound once on `window` (so a fast drag that leaves the track
   * still tracks), and a closure over state would freeze on the first frame. */
  const trackRef = useRef(null)
  const dragRef = useRef(null)
  const [drag, setDrag] = useState(null)   // { mode, id, s, e }

  const minAt = useCallback((clientX) => {
    const el = trackRef.current
    if (!el || !span) return start
    const rect = el.getBoundingClientRect()
    const ratio = clamp((clientX - rect.left) / Math.max(1, rect.width), 0, 1)
    return start + ratio * span
  }, [span, start])

  // The gap this band may occupy: the shift window, minus whatever its
  // neighbours already hold. Clamping here is what makes overlap impossible.
  const gapFor = useCallback((id) => {
    let lo = start; let hi = end
    for (const b of blocks) {
      if (Number(b.row.id) === Number(id)) continue
      if (b.e <= (dragRef.current?.anchorS ?? start)) lo = Math.max(lo, b.e)
    }
    for (const b of blocks) {
      if (Number(b.row.id) === Number(id)) continue
      if (b.s >= (dragRef.current?.anchorS ?? start)) hi = Math.min(hi, b.s)
    }
    return { lo, hi }
  }, [blocks, start, end])

  useEffect(() => {
    function onMove(ev) {
      const d = dragRef.current
      if (!d) return
      ev.preventDefault()
      const cur = snap(minAt(ev.clientX))
      const { lo, hi } = d.gap
      let s = d.s; let e = d.e
      if (d.mode === 'new') {
        s = clamp(Math.min(d.anchor, cur), lo, hi)
        e = clamp(Math.max(d.anchor, cur), lo, hi)
      } else if (d.mode === 'move') {
        const len = d.e - d.s
        s = clamp(cur - d.grab, lo, hi - len)
        e = s + len
      } else if (d.mode === 'start') {
        s = clamp(cur, lo, d.e - MIN_BLOCK_MIN)
        e = d.e
      } else if (d.mode === 'end') {
        s = d.s
        e = clamp(cur, d.s + MIN_BLOCK_MIN, hi)
      }
      const next = { ...d, s, e }
      dragRef.current = next
      setDrag({ mode: next.mode, id: next.id, s, e })
    }

    async function onUp() {
      const d = dragRef.current
      if (!d) return
      dragRef.current = null
      setDrag(null)
      let { s, e } = d
      if (d.mode === 'new') {
        // A click rather than a drag still means "put a break here" — grow it to
        // the minimum instead of writing a zero-length row nobody can grab.
        if (e - s < MIN_BLOCK_MIN) e = Math.min(d.gap.hi, s + MIN_BLOCK_MIN)
        if (e - s < MIN_BLOCK_MIN) return
        await create(s, e)
      } else if (s !== d.orig.s || e !== d.orig.e) {
        await patchWindow(d.id, s, e)
      }
    }

    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }) // re-bound every render on purpose: create/patchWindow close over fresh state

  function beginDrag(ev, mode, block) {
    if (ev.button != null && ev.button !== 0) return
    if (!span || busy) return
    ev.preventDefault(); ev.stopPropagation()
    const cur = snap(minAt(ev.clientX))
    const s = block ? block.s : cur
    const e = block ? block.e : cur
    const id = block ? Number(block.row.id) : 0
    dragRef.current = {
      mode, id, s, e, anchor: cur, anchorS: s, grab: cur - s,
      orig: { s, e }, gap: { lo: start, hi: end },
    }
    dragRef.current.gap = gapFor(id)
    setDrag({ mode, id, s, e })
  }

  async function create(s, e) {
    setBusy(true); setErr('')
    try {
      await postJson('/api/hr/schedule/block', blockPayload({ dateKey, doctorId, branchId, s, e, details: '' }))
      setTick((t) => t + 1); onChanged?.()
    } catch (ex) { setErr(String(ex.message || ex)) }
    finally { setBusy(false) }
  }

  async function patchWindow(id, s, e) {
    setBusy(true); setErr('')
    try {
      await patchJson(`/api/hr/schedule/block/${id}`, {
        start_date: tsOfMin(dateKey, s),
        end_date: tsOfMin(dateKey, e),
        duration: Math.round(e - s),
      })
      setTick((t) => t + 1); onChanged?.()
    } catch (ex) { setErr(String(ex.message || ex)); setTick((t) => t + 1) }
    finally { setBusy(false) }
  }

  async function patchDetails(id, details) {
    setBusy(true); setErr('')
    try {
      await patchJson(`/api/hr/schedule/block/${id}`, { details })
      setTick((t) => t + 1); onChanged?.()
    } catch (ex) { setErr(String(ex.message || ex)) }
    finally { setBusy(false) }
  }

  async function remove(id) {
    setConfirmRow(null)
    setBusy(true); setErr('')
    try {
      await apiDelete(`/api/hr/schedule/block/${id}`)
      setTick((t) => t + 1); onChanged?.()
    } catch (ex) { setErr(String(ex.message || ex)) }
    finally { setBusy(false) }
  }

  function addSuggested() {
    const w = suggestBlock(blocks, start, end, 60)
    if (!w) { setErr('ไม่มีช่วงว่างพอในเวรนี้ — ลบหรือย่อบล็อกเดิมก่อน'); return }
    create(w.s, w.e)
  }

  if (!span) {
    return <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5 text-[12.5px] text-slate-500">
      ระบุช่วงเวลาของเวรให้ถูกต้องก่อน จึงจะเพิ่มบล็อกเวลาได้
    </div>
  }

  const pct = (min) => `${((min - start) / span) * 100}%`

  /* One ruler line carries EVERY time on this control — the shift's own two
   * ends plus a few round hours between them. Labels inside the track (one per
   * hour, plus a second row for the ends) read as two competing scales and
   * crowd the bands they are supposed to measure. The step widens with the span
   * so the line never packs more than a handful of labels, and an interior
   * label too close to an end is dropped rather than overlapped. */
  const labelStep = span <= 180 ? 30 : span <= 360 ? 60 : 120
  const labels = []
  for (let m = Math.ceil(start / labelStep) * labelStep; m < end; m += labelStep) {
    const at = (m - start) / span
    if (at > 0.08 && at < 0.92) labels.push(m)
  }
  // Hour gridlines stay, but silent: they are the ruler's tick marks, not text.
  const ticks = []
  for (let m = Math.ceil(start / 60) * 60; m < end; m += 60) ticks.push(m)

  const view = blocks.map((b) => (
    drag && Number(drag.id) === Number(b.row.id) ? { ...b, s: drag.s, e: drag.e } : b
  ))
  const live = drag && drag.e > drag.s ? drag : null

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        {live ? (
          <span className="text-[12px] font-medium text-rose-700 tabular-nums">
            {fmtMin(live.s)}–{fmtMin(live.e)} · {live.e - live.s} นาที
          </span>
        ) : (
          <span className="text-[11.5px] text-slate-400">
            ลากบนแถบเพื่อสร้างบล็อก · ลากตัวบล็อกเพื่อย้าย · ลากขอบเพื่อปรับความยาว (ทีละ {SNAP_MIN} นาที)
          </span>
        )}
        <button type="button" onClick={addSuggested} disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-50 shrink-0">
          <Plus size={13} /> เพิ่มบล็อก
        </button>
      </div>

      <div className="relative h-4 select-none">
        <span className="absolute left-0 top-0 text-[10.5px] font-medium text-slate-500 tabular-nums">{fmtMin(start)}</span>
        {labels.map((m) => (
          <span key={m} className="absolute top-0 -translate-x-1/2 text-[10.5px] text-slate-400 tabular-nums" style={{ left: pct(m) }}>
            {fmtMin(m)}
          </span>
        ))}
        <span className="absolute right-0 top-0 text-[10.5px] font-medium text-slate-500 tabular-nums">{fmtMin(end)}</span>
      </div>

      <div
        ref={trackRef}
        onPointerDown={(e) => beginDrag(e, 'new', null)}
        className="relative h-12 w-full rounded-xl bg-slate-50 ring-1 ring-slate-200 overflow-hidden select-none cursor-crosshair touch-none">
        {ticks.map((m) => (
          <div key={m} className="absolute inset-y-0 border-l border-slate-200/70" style={{ left: pct(m) }} />
        ))}

        {view.map((b) => (
          <div key={b.row.id}
            onPointerDown={(e) => beginDrag(e, 'move', b)}
            title={`${fmtMin(b.s)}–${fmtMin(b.e)}${b.row.details ? ` · ${b.row.details}` : ''}`}
            className="absolute inset-y-1.5 rounded-md bg-rose-100 ring-1 ring-rose-300 cursor-grab active:cursor-grabbing overflow-hidden touch-none"
            style={{ left: pct(b.s), width: `${((b.e - b.s) / span) * 100}%` }}>
            <span className="absolute inset-y-0 left-0 w-2 cursor-ew-resize bg-rose-300/60 touch-none"
              onPointerDown={(e) => beginDrag(e, 'start', b)} />
            <span className="absolute inset-y-0 right-0 w-2 cursor-ew-resize bg-rose-300/60 touch-none"
              onPointerDown={(e) => beginDrag(e, 'end', b)} />
            <span className="flex h-full items-center justify-center px-2.5 text-[10.5px] font-medium text-rose-800 tabular-nums truncate">
              {b.row.details || `${b.e - b.s} นาที`}
            </span>
          </div>
        ))}

        {drag?.mode === 'new' && drag.e > drag.s && (
          <div className="absolute inset-y-1.5 rounded-md bg-rose-200/70 ring-1 ring-rose-400 pointer-events-none"
            style={{ left: pct(drag.s), width: `${((drag.e - drag.s) / span) * 100}%` }} />
        )}
      </div>

      <div className="mt-2 space-y-1.5">
        {loading && <div className="flex items-center gap-1.5 text-[12.5px] text-slate-400"><Loader2 size={13} className="animate-spin" /> กำลังโหลดบล็อก…</div>}
        {!loading && !blocks.length && (
          <div className="text-[12.5px] text-slate-400 flex items-center gap-1.5"><Ban size={13} /> ยังไม่มีบล็อกเวลาห้ามแทรกในเวรนี้</div>
        )}
        {blocks.map((b) => (
          <div key={b.row.id} className="flex items-center gap-2 rounded-lg ring-1 ring-slate-200 bg-white px-2.5 py-1.5">
            <span className="text-[12.5px] font-medium text-slate-700 tabular-nums shrink-0">{fmtMin(b.s)}–{fmtMin(b.e)}</span>
            <span className="text-[11.5px] text-slate-400 shrink-0">{b.e - b.s} นาที</span>
            <input
              defaultValue={b.row.details || ''}
              onBlur={(e) => { if (e.target.value !== (b.row.details || '')) patchDetails(b.row.id, e.target.value) }}
              placeholder="เหตุผล เช่น พักเที่ยง / ประชุม"
              className="flex-1 min-w-0 rounded-md border border-slate-200 px-2 py-1 text-[12.5px] text-slate-700 outline-none focus:border-brand-400" />
            <button type="button" onClick={() => setConfirmRow(b)} disabled={busy}
              className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 disabled:opacity-50 shrink-0" title="ลบบล็อก">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {err && (
          <div className="flex items-start gap-1.5 text-[12.5px] text-rose-600">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {err}
          </div>
        )}
      </div>

      {confirmRow && (
        <ConfirmDialog
          title="ลบบล็อกเวลาห้ามแทรก"
          message={`${fmtMin(confirmRow.s)}–${fmtMin(confirmRow.e)}${confirmRow.row.details ? ` · ${confirmRow.row.details}` : ''} — ช่วงเวลานี้จะกลับมาให้จองนัดได้ทันที`}
          confirmLabel="ลบบล็อก"
          tone="danger"
          onConfirm={() => remove(confirmRow.row.id)}
          onCancel={() => setConfirmRow(null)}
        />
      )}
    </div>
  )
}
